import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ReportDialog } from '../moderation/ReportDialog';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';
import type { Post } from './api';
import { Verified } from '../../components/ui/Verified';

/**
 * Liking, replying to and reporting an update.
 *
 * ## Why the thread is behind a press
 *
 * A profile is a column of updates, and opening every thread on it would turn a
 * page somebody came to read into a page they have to scroll past. The count is
 * on the button, so the one worth opening announces itself.
 *
 * ## The author's switch is here rather than in the composer
 *
 * Closing a thread is a decision somebody makes *about a conversation*, usually
 * after reading it - not something they predict while writing. So it sits with
 * the thread rather than with the words, and it can be changed as often as they
 * like.
 *
 * Existing comments stay readable when it is off. Nobody's words disappear
 * because the author later changed their mind; the composer is what goes.
 */
interface Comment {
  id: number;
  user_id: number;
  body: string | null;
  deleted: boolean;
  created_at: string;
  author: { username: string | null; display_name: string | null; verified?: boolean } | null;
}

export function PostEngagement({ post, username }: { post: Post; username: string }) {
  const me = useSession((state) => state.user);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [draft, setDraft] = useState('');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['posts', username] });

  const like = useMutation({
    mutationFn: () =>
      post.liked
        ? api.delete(`/posts/${post.id}/like`)
        : api.post(`/posts/${post.id}/like`, {}),
    onSuccess: refresh,
  });

  const { data: thread } = useQuery({
    queryKey: ['post-comments', post.id],
    queryFn: () => api.get<{ items: Comment[] }>(`/posts/${post.id}/comments`),
    // Only once somebody has asked to see it - see the note on the component.
    enabled: open,
  });

  const say = useMutation({
    mutationFn: (body: string) => api.post(`/posts/${post.id}/comments`, { body }),
    onSuccess: async () => {
      setDraft('');
      await queryClient.invalidateQueries({ queryKey: ['post-comments', post.id] });
      await refresh();
    },
  });

  const toggleComments = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch(`/me/posts/${post.id}`, {
        body: post.body,
        visibility: post.visibility,
        comments_enabled: enabled,
      }),
    onSuccess: refresh,
  });

  return (
    <div className="mt-1 flex flex-col gap-3 border-t border-line pt-3">
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          data-post-like={post.id}
          data-liked={post.liked ? 'true' : 'false'}
          disabled={!me || like.isPending}
          onClick={() => like.mutate()}
          className={`meta-caps transition-colors disabled:opacity-50 ${
            post.liked ? 'text-hot-text' : 'text-faint hover:text-ink'
          }`}
        >
          {post.liked ? '♥' : '♡'} {post.like_count > 0 ? post.like_count : ''} Like
        </button>

        {/*
          Absent rather than disabled when the thread is closed - and the count
          goes with it, because "3 comments" you cannot open is a worse answer
          than nothing. See the note on the component about what stays visible.
        */}
        {(post.comments_enabled || post.comment_count > 0) && (
          <button
            type="button"
            data-post-comments={post.id}
            onClick={() => setOpen((current) => !current)}
            className="meta-caps text-faint transition-colors hover:text-ink"
          >
            {post.comment_count > 0 ? `${post.comment_count} ` : ''}
            {post.comment_count === 1 ? 'Comment' : 'Comments'}
          </button>
        )}

        {/*
          Reporting is for other people's updates. Reporting your own is not a
          thing anybody means to do, and offering it invites a support ticket.
        */}
        {me && !post.mine && (
          <button
            type="button"
            data-post-report={post.id}
            onClick={() => setReporting(true)}
            className="meta-caps text-faint transition-colors hover:text-hot-text"
          >
            Report
          </button>
        )}

        {post.mine && (
          <button
            type="button"
            data-post-comments-toggle={post.id}
            disabled={toggleComments.isPending}
            onClick={() => toggleComments.mutate(!post.comments_enabled)}
            className="meta-caps ml-auto text-faint transition-colors hover:text-ink disabled:opacity-50"
          >
            {post.comments_enabled ? 'Turn comments off' : 'Turn comments on'}
          </button>
        )}
      </div>

      {open && (
        <div className="flex flex-col gap-3" data-post-thread={post.id}>
          {(thread?.items ?? []).map((comment) => (
            <div key={comment.id} className="flex flex-col gap-0.5">
              <span className="font-mono text-[11px] text-faint">
                {comment.author?.display_name ?? comment.author?.username ?? 'Someone'}
                {comment.author?.verified && <Verified />}
              </span>
              <p className="text-sm leading-relaxed text-ink">
                {comment.deleted ? <span className="italic text-faint">Removed.</span> : comment.body}
              </p>
            </div>
          ))}

          {thread && thread.items.length === 0 && (
            <p className="font-mono text-[11px] text-soft">Nothing said yet.</p>
          )}

          {post.comments_enabled && me && (
            <form
              className="flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();

                if (draft.trim() !== '') say.mutate(draft.trim());
              }}
            >
              <input
                value={draft}
                maxLength={1000}
                placeholder="Say something"
                data-post-comment-input={post.id}
                onChange={(event) => setDraft(event.target.value)}
                className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-line-strong"
              />
              <button
                type="submit"
                data-post-comment-send={post.id}
                disabled={say.isPending || draft.trim() === ''}
                className="meta-caps rounded-[var(--radius-pill)] bg-accent px-3 text-accent-ink transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                Send
              </button>
            </form>
          )}

          {!post.comments_enabled && (
            <p className="font-mono text-[11px] text-soft" data-post-comments-closed={post.id}>
              Comments are turned off on this update.
            </p>
          )}
        </div>
      )}

      {reporting && (
        <ReportDialog
          subjectType="post"
          subjectId={post.id}
          title="this update"
          onClose={() => setReporting(false)}
        />
      )}
    </div>
  );
}
